import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Row, Space } from 'antd';
import FormInput from 'components/new-ui/composed/formInput/FormInput';
import FormTextArea from 'components/new-ui/composed/formTextArea/FormTextArea';
import { getRequiredMessage } from 'views/declarations/utils/validation-utils';
import Button from 'components/new-ui/base/button/Button';
import { NoteFormFields } from './types';

export const validationSchema = Yup.object().shape({
    title: Yup.string().trim().nullable().required(getRequiredMessage('title')),
    content: Yup.string().trim().nullable().required(getRequiredMessage('content')),
});

const defaultInitialValues: NoteFormFields = {
    title: '',
    content: '',
};

type Props = {
    initialValues?: NoteFormFields & { id?: string };
    onSubmit: (note: NoteFormFields) => Promise<boolean>;
    onCancel?: () => void;
    submitting?: boolean;
};

const CreateUpdateNoteForm = ({ initialValues, submitting, onSubmit, onCancel }: Props) => {
    const formik = useFormik<NoteFormFields>({
        initialValues: initialValues ?? defaultInitialValues,
        validationSchema,
        enableReinitialize: true,
        onSubmit: async (values, helpers) => {
            const ok = await onSubmit(values);
            if (ok) helpers.resetForm();
        },
    });

    const handleCancel = () => {
        formik.resetForm();
        onCancel?.();
    };

    return (
        <form onSubmit={formik.handleSubmit}>
            <Space
                direction="vertical"
                size={16}
                style={{
                    width: '100%',
                }}
            >
                <label
                    style={{
                        marginBottom: 6,
                    }}
                >
                    Title:
                    <FormInput
                        inputStyles={{ maxWidth: '100%' }}
                        fieldProps={formik.getFieldProps('title')}
                        fieldHelper={formik.getFieldHelpers('title')}
                        fieldMeta={formik.getFieldMeta('title')}
                        placeholder="Enter title"
                    />
                </label>

                <label
                    style={{
                        marginBottom: 6,
                    }}
                >
                    Content:
                    <FormTextArea
                        fieldProps={formik.getFieldProps('content')}
                        fieldHelper={formik.getFieldHelpers('content')}
                        fieldMeta={formik.getFieldMeta('content')}
                        placeholder="Enter content"
                    />
                </label>

                <Row justify="end">
                    <Button htmlType="button" onClick={handleCancel}>
                        Cancel
                    </Button>
                    <Button
                        loading={submitting || formik.isSubmitting}
                        disabled={!formik.isValid || !formik.dirty || submitting}
                        htmlType="submit"
                        style={{ marginLeft: 8 }}
                        type="primary"
                    >
                        Save
                    </Button>
                </Row>
            </Space>
        </form>
    );
};

export default CreateUpdateNoteForm;
